import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import DatePicker from "~/components/DatePicker"
import { cn } from "@/lib/utils"

export const CLEAN_KINDS = [
  { value: "back_to_back", label: "Back-to-back", hint: "Checkout day, between 11am and 3pm — new guests arrive that afternoon." },
  { value: "scheduled", label: "Set date and time", hint: "One agreed day, with a time if you have one." },
  { value: "changeover", label: "Either of two days", hint: "From 11am on the first day to 3pm on the second — the 24-hour changeover cleaners like." },
  { value: "cleaner_chooses", label: "Cleaner to confirm date and time", hint: "They pick any day in the window from their cleans page and you get a notification." },
]

export const CLEAN_KIND_COLOURS = {
  back_to_back: "bg-orange-200 text-orange-900 border-orange-300 dark:bg-orange-900/50 dark:text-orange-100 dark:border-orange-700",
  scheduled: "bg-sky-200 text-sky-900 border-sky-300 dark:bg-sky-900/50 dark:text-sky-100 dark:border-sky-700",
  changeover: "bg-violet-200 text-violet-900 border-violet-300 dark:bg-violet-900/50 dark:text-violet-100 dark:border-violet-700",
  cleaner_chooses: "bg-teal-200 text-teal-900 border-teal-300 dark:bg-teal-900/50 dark:text-teal-100 dark:border-teal-700",
}

export const CLEAN_KIND_LABELS = Object.fromEntries(CLEAN_KINDS.map((k) => [ k.value, k.label ]))

export function addDays(iso, days) {
  const date = new Date(`${iso}T00:00:00`)
  date.setDate(date.getDate() + days)
  return date.toLocaleDateString("en-CA")
}

export function shortDay(iso) {
  return iso ? new Date(`${iso}T00:00:00`).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" }) : ""
}

// The kind picker plus the date/time inputs each kind needs. `form` holds
// kind, window_starts_on, window_ends_on, scheduled_time; the caller owns the state.
export default function CleanKindFields({ form, onChange, minDate }) {
  const set = (patch) => onChange({ ...form, ...patch })
  const dayTwo = form.window_starts_on ? addDays(form.window_starts_on, 1) : ""
  const windowEnd = form.window_ends_on && form.window_ends_on > form.window_starts_on ? form.window_ends_on : addDays(form.window_starts_on || new Date().toLocaleDateString("en-CA"), 7)

  return (
    <div className="space-y-3">
      <div role="radiogroup" className="grid gap-2">
        {CLEAN_KINDS.map((option) => (
          <label
            key={option.value}
            className={cn("flex items-start gap-2 rounded-lg border p-2.5 text-sm", form.kind === option.value ? "border-brand bg-brand/10" : "border-border")}
          >
            <input type="radio" name="clean-kind" value={option.value} checked={form.kind === option.value} onChange={() => set({ kind: option.value })} className="mt-0.5 h-4 w-4" />
            <span>
              <span className="font-medium text-foreground">{option.label}</span>
              <span className="block text-xs text-muted-foreground">{option.hint}</span>
            </span>
          </label>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>{form.kind === "changeover" ? "First day" : form.kind === "back_to_back" ? "Checkout day" : form.kind === "cleaner_chooses" ? "Window opens" : "Date"}</Label>
          <div className="mt-1">
            <DatePicker value={form.window_starts_on} min={minDate} onChange={(value) => set({ window_starts_on: value })} />
          </div>
        </div>
        {form.kind === "back_to_back" && (
          <p className="self-end rounded-lg border border-orange-200 bg-orange-50 p-2 text-sm text-orange-800 dark:border-orange-900 dark:bg-orange-950/40 dark:text-orange-200">11am – 3pm</p>
        )}
        {form.kind === "changeover" && (
          <div>
            <Label>Second day</Label>
            <p className="mt-1 rounded-lg border border-border bg-muted/50 px-3 py-2 text-sm text-foreground">{shortDay(dayTwo) || "—"}</p>
            <p className="mt-1 text-xs text-muted-foreground">11am {shortDay(form.window_starts_on).split(" ")[0]} → 3pm {shortDay(dayTwo).split(" ")[0]}</p>
          </div>
        )}
        {form.kind === "cleaner_chooses" && (
          <div>
            <Label>Window closes</Label>
            <div className="mt-1">
              <DatePicker value={windowEnd} min={form.window_starts_on} onChange={(value) => set({ window_ends_on: value })} />
            </div>
            <p className="mt-1 text-xs text-muted-foreground">Usually the day before the next guests arrive.</p>
          </div>
        )}
        {form.kind === "scheduled" && (
          <div>
            <Label htmlFor="scheduled_time">Time</Label>
            <Input id="scheduled_time" type="time" value={form.scheduled_time || ""} onChange={(e) => set({ scheduled_time: e.target.value })} className="mt-1" />
          </div>
        )}
      </div>
    </div>
  )
}
